import React, { useContext, useState, useLayoutEffect } from 'react'
import styled, { css } from 'styled-components'
import { Arrow } from '../../../assets/svg/criticalSVG'
import PropTypes from 'prop-types'

import Accordion from '../../Accordion'
import Button from '../../Button'
import Tooltip from '../Tooltip'

import MessagesContext from '../../../context/MessagesContext'

const StyledWrapper = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid #fbfbfc;
  border-radius: 8px;
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: 0 auto 20px;
  max-width: 617px;
  padding: 20px 0;
  text-align: center;
  width: 100%;

  ${({ isRecommended }) =>
    isRecommended
      ? css`
          box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
            0 0 1px 0 rgba(10, 22, 70, 0.06),
            0 28px 70px -7px rgba(5, 15, 54, 0.16);
        `
      : css`
          box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
            0 0 1px 0 rgba(10, 22, 70, 0.06);
        `}

  > header {
    padding: 0 50px;

    h3 {
      color: #0a1f44;
      font-size: 18px;
      font-stretch: normal;
      font-style: normal;
      font-weight: 500;
      letter-spacing: normal;
      line-height: 1.67;
      margin: 0 0 8px;
      text-align: center;
    }

    p {
      color: #4e5d78;
      font-size: 12px;
      font-stretch: normal;
      font-style: normal;
      font-weight: normal;
      height: 0px;
      letter-spacing: normal;
      line-height: 1.67;
      margin: 0 0 16px;
      text-align: center;
    }
  }

  > section {
    padding: 20px 20px 0;
    width: 100%;
  }

  a {
    width: 100%;
  }

  :last-of-type {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.media.desktop} {
    border-radius: 0;
    margin-bottom: 0;
    padding: 46px 0 44px;

    ${({ isRecommended }) =>
      !isRecommended && 'max-width: 310px; box-shadow: none;'}

    ${({ isRecommended }) =>
      isRecommended &&
      css`
        box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
          0 0 1px 0 rgba(10, 22, 70, 0.06),
          0 28px 70px -7px rgba(5, 15, 54, 0.16);
        transform: scale(1.05);
        width: 350px;
      `}

    > section {
      padding: 14px 46px 0;
      width: 100%;
    }

    :last-of-type {
      margin-right: 0;
    }
  }
`

const StyledIcon = styled.img.attrs(props => ({
  src: props.src,
  alt: props.alt,
}))`
  margin: 0 0 8px;
`

const StyledTextPrice = styled.div`
  margin: 100px auto 30px;

  ${({ theme }) => theme.media.tablet} {
    margin: 60px auto 30px;
  }

  ${({ theme }) => theme.media.desktop} {
    margin-top: 80px;
  }
`

const StyledSubtitle = styled.div`
  -webkit-letter-spacing: 1.2px;
  -moz-letter-spacing: 1.2px;
  -ms-letter-spacing: 1.2px;
  color: #97a1b2;
  font-size: 0.625em;
  font-weight: 700;
  letter-spacing: 1.2px;
  line-height: 16px;
  margin-bottom: 0;
  bottom: 0;
  text-align: center;
  text-transform: uppercase;
`

const FlexPrice = styled.div`
  color: #0a1f44;
  display: inline-block;
  font-feature-settings: 'lnum';
  font-size: 28px;
  font-stretch: normal;
  font-style: normal;
  font-variant-numeric: lining-nums;
  font-weight: 400;
  letter-spacing: -1.5px;
  line-height: 1;
  position: relative;

  ${({ theme }) => theme.media.desktop} {
    margin: 30px auto;
  }
`

const StyledPrice = styled.div`
  color: #0a1f44;
  display: inline-block;
  font-feature-settings: 'lnum';
  font-size: 30px;
  font-stretch: normal;
  font-style: normal;
  font-variant-numeric: lining-nums;
  font-weight: 400;
  letter-spacing: -1.5px;
  line-height: 1;
  margin: 10px 0;
  padding-left: 12px;
  position: relative;

  :before {
    color: #0a1f44;
    content: '$';
    font-size: 16px;
    font-stretch: normal;
    font-style: normal;
    font-weight: normal;
    left: 0;
    letter-spacing: normal;
    line-height: 1.71;
    position: absolute;
    top: 0;
  }

  :after {
    color: #4e5d78;
    content: '/ month';
    font-size: 16px;
    font-stretch: normal;
    font-style: normal;
    font-weight: normal;
    letter-spacing: normal;
    line-height: 1.88;
    margin-left: 8px;
  }
`

const StyledButton = styled.div`
  display: block;
  margin-bottom: 30px;
`

const StyleBottomButton = styled.div`
  display: none;

  ${({ theme }) => theme.media.desktop} {
    display: block;
  }
`

const StyledFeatures = styled(Accordion)`
  border-bottom: 1px solid rgba(201, 206, 214, 0.4);
  width: 100%:

  ${({ theme }) => theme.media.desktop} {
    border-bottom: 0;
  }
`

const StyledFeaturesHeader = styled.div`
  border-top: 1px solid rgba(201, 206, 214, 0.4);
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  padding: 20px;
  position: relative;
  text-align: left;
  width: 100%;

  > svg {
    transition: transform 150ms ease;
    ${({ expanded }) => expanded && 'transform: rotate(180deg);'}
  }

  ${({ expanded }) =>
    !expanded && 'border-bottom: 1px solid rgba(201, 206, 214, 0.4);'}

  ${({ theme }) => theme.media.desktop} {
    display: none;
  }
`

const StyledFeaturesList = styled.ul`
  border-bottom: 1px solid rgba(201, 206, 214, 0.4);
  width: 100%;
  z-index: 1;

  ${({ theme }) => theme.media.desktop} {
    border-bottom: none;
  }
`

const StyledFeature = styled.li`
  color: ${({ active }) => (active ? '#0a1f44' : '#8a94a6')};
  font-size: 12px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  height: 24px;
  letter-spacing: normal;
  line-height: 1.67;
  margin: 0 8px 8px;
  text-align: center;
  z-index: 1;

  :before {
    color: #63bb43;
    content: '✓';
    display: ${({ active }) => (active ? 'inline-block' : 'none')};
    font-size: 14px;
    font-stretch: normal;
    font-style: normal;
    font-weight: 800;
    letter-spacing: normal;
    line-height: 1.71;
    margin-right: 8px;
  }

  ${({ theme }) => theme.media.desktop} {
    display: block;
    z-index: 1;
  }
`

const StyledExtra = styled.div`
  font-size: 12px;
  font-weight: normal;
  line-height: 1.67;
  margin: 24px 0 0;
  text-align: center;

  h4 {
    color: #4e5d78;
    margin: 0 0 4px;
  }

  > div {
    color: #8a94a6;
    margin: 0 0 2px;

    > span {
      color: #4e5d78;
    }
  }

  :last-of-type {
    margin-bottom: 0;
  }
`

const Plan = ({ data, recommended = {} }) => {
  const {
    activeFeatures,
    additionalValidation,
    button,
    disabledFeatures,
    icon,
    name,
    options,
    price,
    subtitle,
    type,
  } = data

  const { messages } = useContext(MessagesContext)

  const [featuresExpanded, setFeatureExpanded] = useState(false)
  const initialOptionsList = options.filter(opt => opt.messages <= messages)
  const initialOption = initialOptionsList.length
    ? initialOptionsList[initialOptionsList.length - 1]
    : options[0]

  const [signUpLink, setSignUpLink] = useState(initialOption.signUpLink)

  const [estimate, setEstimate] = useState(price)
  const [, setEmailFeature] = useState(activeFeatures[0].name)

  const additionalMessage = options[0].additionalMessage
  const isRecommended = data.id === recommended.planId

  useLayoutEffect(() => {
    const matchingOption = options.find(item => item.name === recommended.name)

    if (!matchingOption || recommended.name === 'Enterprise') {
      return
    }

    setEmailFeature(
      `${matchingOption.messages.toLocaleString()} Emails Included`
    )
  }, [options, recommended])

  useLayoutEffect(() => {
    const newOptionsList = options.filter(opt => opt.messages <= messages)

    const newOption = newOptionsList.length
      ? newOptionsList[newOptionsList.length - 1]
      : options[0]

    setSignUpLink(newOption.signUpLink)
  }, [messages, options])
  
  useLayoutEffect(() => {
    if (data.id !== recommended.id || estimate === recommended.priceEstimate) {
      return
    }

    setEstimate(recommended.priceEstimate)
  }, [data, recommended, estimate])

  let foundationEst
  let growthEst
  let scaleEst

  if (messages >= 0 && messages <= 99000) {
    foundationEst = 35
    growthEst = 80
    scaleEst = 90
  } else if (messages >= 100000 && messages <= 249000) {
    foundationEst = 75
    growthEst = 80
    scaleEst = 90
  } else if (messages >= 250000 && messages <= 499000) {
    foundationEst = 75
    growthEst = 200
    scaleEst = 215
  } else if (messages >= 500000 && messages <= 749000) {
    foundationEst = 75
    growthEst = 355
    scaleEst = 400
  } else if (messages >= 750000 && messages <= 999000) {
    foundationEst = 75
    growthEst = 500
    scaleEst = 550
  } else if (messages >= 1000000 && messages <= 2000000) {
    foundationEst = 75
    growthEst = 650
    scaleEst = 700
  } else if (messages >= 2500000) {
    foundationEst = 75
    growthEst = 650
    scaleEst = 1250
  } else {
    foundationEst = 75
    growthEst = 1550
    scaleEst = 1250
  }

  // emails included variables
  let foundationMsg
  let growthMsg
  let scaleMsg

  if (messages >= 0 && messages <= 75000) {
    foundationMsg = "50,000"
    growthMsg = "100,000"
    scaleMsg = "100,000"
  } else if (messages >= 100000 && messages <= 200000) {
    foundationMsg = "100,000"
    growthMsg = "100,000"
    scaleMsg = "100,000"
  } else if (messages >= 250000 && messages <= 475000) {
    foundationMsg = "100,000"
    growthMsg = "250,000"
    scaleMsg = "250,000"
  } else if (messages === 500000) {
    foundationMsg = "100,000"
    growthMsg = "500,000"
    scaleMsg =  "500,000"
  } else if (messages === 750000) {
    foundationMsg = "100,000"
    growthMsg = "750,000"
    scaleMsg =  "750,000"
  } else if (messages >= 1000000 && messages <= 2000000) {
    foundationMsg = "100,000"
    growthMsg = "1,000,000"
    scaleMsg =  "1,000,000"
  } else {
    foundationMsg = "100,000"
    growthMsg = "1,000,000"
    scaleMsg =  "2,500,000"
  }

  // TODO: Fill in all additionalMessage
  if (additionalMessage === null || additionalValidation === null) {
    return <span />
  }

  return (
    <StyledWrapper isRecommended={isRecommended}>
      <header>
        <StyledIcon alt={name} src={icon.file.url} />
        <h3>{name}</h3>
        <p>{subtitle}</p>
        <StyledTextPrice>
          {data.name === 'Trial' ? (
            <StyledSubtitle />
          ) : (
            <StyledSubtitle>Starting at</StyledSubtitle>
          )}

          {data.name === 'Trial' && <FlexPrice>Pay As You Grow</FlexPrice>}
          {data.name === 'Foundation' && (
            <StyledPrice>{foundationEst}</StyledPrice>
          )}
          {data.name === 'Growth' && <StyledPrice>{growthEst}</StyledPrice>}
          {data.name === 'Scale' && <StyledPrice>{scaleEst}</StyledPrice>}

          
          {data.name === 'Foundation' &&
            <StyledSubtitle>{foundationMsg} emails included</StyledSubtitle>
          }
          {data.name === 'Growth' &&
            <StyledSubtitle>{growthMsg} emails included</StyledSubtitle>
          }
          {data.name === 'Scale' &&
            <StyledSubtitle>{scaleMsg} emails included</StyledSubtitle>
          }
        </StyledTextPrice>
        {/* Sign Up Button */}
        <StyledButton>
          <Button
            external
            to={signUpLink}
            ghost={button.theme.indexOf('ghost') > -1 ? 'true' : undefined}
            primary={button.theme.indexOf('primary') > -1 ? 'true' : undefined}
            isAbsolutePath="true"
          >
            {button.buttonText}
          </Button>
        </StyledButton>
      </header>

      <StyledFeatures
        onlyMobile
        allowOverflow={true}
        active={featuresExpanded}
        header={
          <StyledFeaturesHeader expanded={featuresExpanded}>
            <span>
              {featuresExpanded ? 'Hide features' : 'Expand features'}
            </span>{' '}
            <Arrow />
          </StyledFeaturesHeader>
        }
        onClick={() => setFeatureExpanded(!featuresExpanded)}
      >
        <StyledFeaturesList>
          {activeFeatures.map(feature =>
            feature.tooltip ? (
              <StyledFeature key={feature.id} active={true}>
                {feature.name}
                <Tooltip>
                  <p>{feature.tooltip}</p>
                </Tooltip>
              </StyledFeature>
            ) : (
              <StyledFeature key={feature.id} active={true}>
                {feature.name}
              </StyledFeature>
            )
          )}
          {disabledFeatures &&
            disabledFeatures.map(feature =>
              feature.tooltip ? (
                <StyledFeature key={feature.id} active={false}>
                  {feature.name}
                  <Tooltip>{feature.tooltip}</Tooltip>
                </StyledFeature>
              ) : (
                <StyledFeature key={feature.id} active={false}>
                  {feature.name}
                </StyledFeature>
              )
            )}
        </StyledFeaturesList>
      </StyledFeatures>

      <section>
        {/* Sign Up Button */}
        <StyleBottomButton>
          <Button
            external
            to={signUpLink}
            ghost={button.theme.indexOf('ghost') > -1 ? 'true' : undefined}
            primary={button.theme.indexOf('primary') > -1 ? 'true' : undefined}
            isAbsolutePath="true"
          >
            {button.buttonText}
          </Button>
        </StyleBottomButton>
        {/* Extras */}
        <StyledExtra>
          <h4>
            {name !== 'Trial' && type === 'email + validation' ? 'Extra emails & validations' : 'Extra emails beyond trial volume'}
          </h4>
          <>
            <div>
              from{' '}
              <span>${parseFloat(additionalMessage[0].price).toFixed(2)}</span>{' '}
              / 1000 emails{' '}
              {additionalMessage.length && (
                <Tooltip>
                  <h6>{name}</h6>
                  <p>Additional messages</p>
                  <table>
                    <thead>
                      <tr>
                        <th>Tier:</th>
                        <th>$ / 1000</th>
                      </tr>
                    </thead>
                    <tbody>
                      {additionalMessage.map((item, index) => (
                        <tr key={item.amount + index}>
                          <td>
                            {item.text}{' '}
                            {additionalMessage.length === index && item.amount}
                          </td>
                          <td>
                            <span>${parseFloat(item.price).toFixed(2)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Tooltip>
              )}
            </div>
            {name !== 'Trial' ? (
              <div>
                from{' '}
                <span>
                  ${name !== 'Scale' ? (
                    1.20.toFixed(2)
                  ) : (
                    0.80.toFixed(2)
                  )}
                </span>
                {' '} / 100 validations{' '}
                {additionalValidation && (
                  <Tooltip>
                    <h6>{name}</h6>
                    <p>Validations pricing</p>
                    <table>
                      <thead>
                        <tr>
                          <th>Tier:</th>
                          <th>$ / 100</th>
                        </tr>
                      </thead>
                      <tbody>
                        {additionalValidation.map((item, index) => (
                          <tr key={item.amount + index}>
                            <td>
                              {item.text}{' '}
                              {additionalValidation.length !== index &&
                                item.amountText}
                            </td>
                            <td>
                              <span>${parseFloat(item.price).toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Tooltip>
                )}
              </div>
            ) : (
              <div></div>
            )}
          </>
        </StyledExtra>
      </section>
    </StyledWrapper>
  )
}

Plan.propTypes = {
  data: PropTypes.object.isRequired,
}
export default Plan